import { Button } from "@ascleo/ui/components/ui/button";
import type { FieldOutcome } from "@ascleo/frontdesk-contracts/patient-info/submit";
import { AlertCircle, Check, RotateCcw, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CategoryCard } from "./CategoryCard";
import { maskValue } from "./masking";
import type { SubmittedEntry } from "./submitted-proposal";

const CHIP_STYLE: Readonly<Record<FieldOutcome, string>> = {
  updated: "bg-success-subtle text-success-ink",
  satisfied: "bg-success-subtle text-success-ink",
  "task-created": "bg-brand-supporting-subtle text-brand-supporting-ink",
  "not-saved": "bg-destructive-subtle text-destructive-ink",
  "pending-retry": "bg-destructive-subtle text-destructive-ink",
};

function OutcomeIcon({ outcome }: { outcome: FieldOutcome }) {
  if (outcome === "updated" || outcome === "satisfied")
    return <Check className="size-4" aria-hidden="true" />;
  if (outcome === "task-created")
    return <Send className="size-4" aria-hidden="true" />;
  return <AlertCircle className="size-4" aria-hidden="true" />;
}

/**
 * One submitted field and what actually became of it, on the same row shape as the review.
 *
 * Keeping the check-sheet structure is the point: a detached list of sentences cannot tell the
 * patient *which* of their numbers is still unsaved. Retry is offered only for `pending-retry`,
 * the sole non-terminal outcome — `not-saved` is the server's final word, so a button there would
 * be a control that visibly does nothing. Phone numbers and e-mail addresses stay masked here too,
 * like everywhere else on this shared screen (KC-56).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <OutcomeRow entry={entry} outcome="pending-retry" retrying={false} onRetry={() => {}} />
 * ```
 */
export function OutcomeRow({
  entry,
  outcome,
  retrying,
  onRetry,
}: {
  entry: SubmittedEntry;
  outcome: FieldOutcome;
  retrying: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const failed = outcome === "pending-retry" || outcome === "not-saved";

  return (
    <CategoryCard
      category={entry.category}
      tone={failed ? "editing" : "resting"}
      chip={
        <span
          className={`inline-flex min-h-8 items-center gap-2 rounded-full px-3 py-1 text-base font-medium ${CHIP_STYLE[outcome]}`}
          data-testid="patient-info-outcome-chip"
          data-outcome={outcome}
        >
          <OutcomeIcon outcome={outcome} />
          {t(`self-service.check-in.patient-info.result.outcome.${outcome}`)}
        </span>
      }
      actions={
        outcome === "pending-retry" ? (
          <Button
            type="button"
            size="2xl"
            disabled={retrying}
            onClick={onRetry}
            data-testid="patient-info-outcome-retry-button"
          >
            <RotateCcw aria-hidden className="size-6" />
            {t("self-service.check-in.patient-info.result.retry")}
          </Button>
        ) : undefined
      }
    >
      <span className="flex min-w-0 flex-wrap items-baseline gap-2">
        {entry.typeKey !== undefined && (
          <span className="text-base text-foreground">
            {t(`self-service.check-in.patient-info.review.type.${entry.typeKey}`)}
          </span>
        )}
        <span className="text-xl break-all" data-testid="patient-info-outcome-value">
          {entry.kind === "delete"
            ? t("self-service.check-in.patient-info.result.removed")
            : entry.displayValue === undefined
              ? t("self-service.check-in.patient-info.result.cleared")
              : entry.category === "hausarzt"
                ? entry.displayValue
                : maskValue(entry.displayValue)}
        </span>
      </span>

      {outcome === "task-created" && (
        <span
          className="text-lg text-foreground"
          data-testid="patient-info-outcome-note"
        >
          {t("self-service.check-in.patient-info.result.task-note-v2")}
        </span>
      )}
      {outcome === "not-saved" && (
        <span
          className="text-lg text-destructive-ink"
          data-testid="patient-info-outcome-note"
        >
          {t("self-service.check-in.patient-info.result.not-saved-note")}
        </span>
      )}
    </CategoryCard>
  );
}
